import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setTopNavbar } from '../../Reducers/actions/navbarActions';
import { getShippingMethods, deleteShippingMethod, openShippingMethodForm, closeShippingMethodForm } from '../../Reducers/actions/shippingMethodsActions';
import Icon from '../../components/Icon/Icon';
import Skeleton from '../../components/Skeleton/Skeleton';
import AddEditShippingMethod from '../../components/AddEditShippingMethod/AddEditShippingMethod';
import ModalMessage from '../../components/ModalMessage/ModalMessage';
import './ShippingMethods.scss';

function ShippingMethodsListSkeleton() {
    return (
        <div className="shipping-methods-container" role="status" aria-label="Loading shipping methods">
            <div className="shipping-methods-list px-16">
                <Skeleton width="72px" height="12px" borderRadius="6px" className="m-0 p-0 mb-8" />
                <Skeleton width="100%" height="14px" borderRadius="6px" className="m-0 p-0 mb-16" />
                {[1, 2, 3].map((i) => (
                    <div key={i} className="shipping-method-card mb-8" aria-hidden>
                        <div className="method-content">
                            <Skeleton width="75%" height="14px" borderRadius="6px" className="m-0 p-0 mb-8" />
                            <Skeleton width="55%" height="12px" borderRadius="6px" className="m-0 p-0" />
                        </div>
                        <div className="method-actions d-flex aic">
                            <Skeleton width="20px" height="20px" borderRadius="4px" className="m-0 p-0 mr-8" />
                            <Skeleton width="20px" height="20px" borderRadius="4px" className="m-0 p-0" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

const ShippingMethods = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { methods, loading, showAddForm } = useSelector((state) => state.shippingMethods || { methods: [], loading: false, showAddForm: false });
    const [editingMethod, setEditingMethod] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [methodToDelete, setMethodToDelete] = useState(null);

    useEffect(() => {
        dispatch(setTopNavbar({
            title: 'SHIPPING METHODS',
            showBackArrow: true,
        }));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getShippingMethods());
    }, [dispatch]);

    // Handle browser back button
    useEffect(() => {
        const handlePopState = () => {
            if (showAddForm) {
                dispatch(closeShippingMethodForm());
                setEditingMethod(null);
            }
        };

        if (showAddForm) {
            window.addEventListener('popstate', handlePopState);
        }

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [showAddForm, dispatch]);

    const handleAdd = () => {
        setEditingMethod(null);
        dispatch(openShippingMethodForm());
    };

    const handleEdit = (method) => {
        setEditingMethod(method);
        dispatch(openShippingMethodForm());
    };

    const handleDelete = (method) => {
        setMethodToDelete(method);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!methodToDelete) return;
        try {
            const result = await dispatch(deleteShippingMethod(methodToDelete.shipping_method_id || methodToDelete.id));
            if (result?.success) {
                setShowDeleteModal(false);
                setMethodToDelete(null);
                dispatch(getShippingMethods());
            }
        } catch (error) {
            console.error('Error deleting shipping method:', error);
        }
    };

    const handleSaveSuccess = () => {
        dispatch(closeShippingMethodForm());
        setEditingMethod(null);
        dispatch(getShippingMethods());
    };

    // Show add/edit form
    if (showAddForm) {
        return (
            <AddEditShippingMethod
                method={editingMethod}
                onSave={handleSaveSuccess}
                onCancel={() => {
                    dispatch(closeShippingMethodForm());
                    setEditingMethod(null);
                }}
            />
        );
    }

    if (loading && !methods?.length) {
        return <ShippingMethodsListSkeleton />;
    }

    // Show empty state or list view
    return (
        <div className="shipping-methods-container">
            {!methods || methods.length === 0 ? (
                <div className="shipping-methods-empty">
                    <div className="empty-state-icon">
                        <Icon alias="shipment" color="#FF3232" width={120} height={120} />
                    </div>
                    <div className="empty-state-box">
                        <p className="fs-regular fw-bold fg-dark mb-8">Add a shipping method</p>
                        <p className="fs-xs fg-dark fw-medium">
                            Manage how you deliver your creations
                        </p>
                    </div>
                    <button
                        className="btn red-btn btn-full"
                        onClick={handleAdd}
                        style={{ height: '64px', marginTop: '24px' }}
                    >
                        ADD A SHIPPING METHOD
                    </button>
                </div>
            ) : (
                <div className="shipping-methods-list ">
            
                    {/* General Methods Section */}
                    {methods.some(method => method.type === 'general') && (
                        <div className="shipping-methods-section">
                            <p className="section-title fs-xs fw-bold fg-dark ">
                               General
                            </p>
                            <p className='fs-xs fw-medium fg-dark'> Delivery methods that are based on geography</p>
                            {methods
                                .filter(method => method.type === 'general')
                                .map((method) => (
                                    <ShippingMethodCard
                                        key={method.shipping_method_id || method.id}
                                        method={method}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                    />
                                ))}
                        </div>
                    )}

                    {/* Specific Methods Section */}
                    {methods.some(method => method.type === 'specific') && (
                        <div className="shipping-methods-section">
                            <p className="section-title fs-xs fw-bold fg-dark">
                                Specific
                            </p>
                            <p className='fs-xs fw-medium fg-dark'>Based on creation type and geography</p>

                            {methods
                                .filter(method => method.type === 'specific')
                                .map((method) => (
                                    <ShippingMethodCard
                                        key={method.shipping_method_id || method.id}
                                        method={method}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                    />
                                ))}
                        </div>
                    )}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <ModalMessage onClose={() => setShowDeleteModal(false)}>
                    <div className="delete-confirmation d-flex f-column aic jcc">
                        <p className="fs-sm2 fw-bold mb-32 mt-32">
                            Are you sure you want to delete this shipping method?
                        </p>
                        <button
                            className="btn red-outline-btn btn-full aic jcc fw-extra-bold mb-24"
                            style={{ height: '64px' }}
                            onClick={confirmDelete}
                        >
                            YES, DELETE
                        </button>
                        <p
                            className="fs-xxs fg-dark fw-bold mb-16 mt-0 c-pointer td-underline"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            CANCEL
                        </p>
                    </div>
                </ModalMessage>
            )}
        </div>
    );
};

const ShippingMethodCard = ({ method, onEdit, onDelete }) => {
    const formatDestinations = (destinations) => {
        if (!destinations || destinations.length === 0) return 'No destination';
        if (destinations.length <= 2) {
            return destinations.map(d => d.country_name || d).join(', ');
        }
        return `${destinations.slice(0, 2).map(d => d.country_name || d).join(', ')} + ${destinations.length - 2} more`;
    };

    const formatRates = (rates) => {
        if (!rates || rates.length === 0) return 'No rates';
        const rateStrings = rates.map(rate => {
            let str = `€${parseFloat(rate.rate || 0).toFixed(2)}`;
            if (rate.additional_item_rate) {
                str += ` (+€${parseFloat(rate.additional_item_rate).toFixed(2)})`;
            }
            return str;
        });
        if (rateStrings.length <= 2) {
            return rateStrings.join(' ');
        }
        return `${rateStrings.slice(0, 2).join(' ')} + ${rateStrings.length - 2} more`;
    };

    return (
        <div className="shipping-method-card">
            <div className="method-content">
                {method.type === 'specific' && method.title && (
                    <p className="method-title fs-xs fw-bold fg-dark mb-8">{method.title}</p>
                )}
                <p className="method-destinations fs-xs fg-dark fw-bold mb-8">
                    {formatDestinations(method.destinations)}
                </p>
                <p className="method-rates fs-xs fg-dark fw-medium">
                    {formatRates(method.rates)}
                </p>
            </div>
            <div className="method-actions">
                <Icon
                    alias="edit_v2"
                    width={20}
                    height={20}
                    color="#FF3232"
                    stroke="#FF3232"
                    strokeWidth={2}
                    className="c-pointer mr-8"
                    onClick={() => onEdit(method)}
                />
                <Icon
                    alias="trash_v2"
                    width={20}
                    height={20}
                    color="#FF3232"
                    stroke="#FF3232"
                    strokeWidth={2}
                    className="c-pointer"
                    onClick={() => onDelete(method)}
                />
            </div>
        </div>
    );
};

export default ShippingMethods;

